import { useLazyQuery, gql } from '@apollo/client';

import { UserAccessDetails } from '../types/AccessControl';

const GET_USER_ACCESS_DETAILS = gql`
  query GetUserAccessDetails($params: UserAccessDetailsInput!) {
    getUserAccessDetails(params: $params) {
      data {
        id
        name
        primaryEmail
        image
        logo
        accessUsers {
          syncStatus
          channelId
          mobileAccess
          accessControlGroups {
            id
            name
          }
        }
        companies {
          id
          name
        }
        accessRole {
          userId
          channelId
          accessRoles
        }
        credentialGroups {
          id
          vtsUserId
          vtsOrganizationId
          provider
          type
          status
          credentials {
            mapId
            vtsOrganizationId
            cardNumber
            provider
            status
            credentialType
            activatedAt
            revokedAt
          }
        }
      }
      pageInfo {
        currentPage
        pageSize
        totalCount
      }
    }
  }
`;

interface UserAccessDetailsResponse {
  data: UserAccessDetails[];
  pageInfo: {
    currentPage?: number;
    pageSize?: number;
    totalCount?: number;
  };
  loading: boolean;
  error: Error | null;
}

export function useUsersAccessDetails(channelId: string) {
  const [getUserAccessDetails, { data, loading, error }] = useLazyQuery(
    GET_USER_ACCESS_DETAILS
  );

  const prepareResponsePayload = (
    userAccessDetails: any
  ): Omit<UserAccessDetailsResponse, 'loading' | 'error'> => {
    if (!userAccessDetails?.getUserAccessDetails) {
      return {
        data: [],
        pageInfo: {},
      };
    }

    return {
      data: userAccessDetails.getUserAccessDetails.data,
      pageInfo: userAccessDetails.getUserAccessDetails.pageInfo,
    };
  };

  const fetchUserAccessDetails = async (params?: {
    pagination?: {
      page: number;
      limit: number;
    };
    sort: {
      field: string;
      direction: 'asc' | 'desc';
    } | null;
    search: string | null;
  }): Promise<UserAccessDetailsResponse> => {
    try {
      const paginationPayload = params ? {
        ...(params.pagination && {
          pagination: {
            page: params.pagination.page,
            limit: params.pagination.limit,
          },
        }),
        sort: params.sort,
        search: params.search,
        withUserMappings: true
      }: {};

      const { data: userAccessDetails, error: fetchError } =
        await getUserAccessDetails({
          variables: {
            params: {
              channelId,
              ...paginationPayload
            },
          },
          fetchPolicy: 'no-cache',
        });

      if (fetchError) {
        throw fetchError;
      }

      return {
        ...prepareResponsePayload(userAccessDetails),
        loading: false,
        error: null,
      };
    } catch (err) {
      console.error('Error fetching user access details:', err);

      return {
        data: [],
        pageInfo: {},
        loading: false,
        error: err as Error,
      };
    }
  };

  return {
    fetchData: fetchUserAccessDetails,
    ...prepareResponsePayload(data),
    loading,
    error,
  };
}
